import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  defaultRangeExtractor,
  useVirtualizer,
  type Range,
  type VirtualItem,
} from '@tanstack/react-virtual';

/** Opt-in row virtualization for `DataTable`. */
export interface DataTableVirtualization {
  /** First guess at a row's height in px; each row is measured once drawn. */
  estimateRowHeight: number;
  /** Rows drawn past each edge of the viewport. Defaults to 10. */
  overscan?: number;
}

const DEFAULT_OVERSCAN = 10;

/**
 * The nearest ancestor that scrolls vertically. The table's own wrapper is
 * skipped: it scrolls sideways only and grows with its rows.
 */
export function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
}

/** One `<tr>` of a windowed body: a drawn item, or a spacer for undrawn ones. */
export type WindowSegment =
  | { kind: 'spacer'; id: string; height: number; start: boolean }
  | { kind: 'item'; item: VirtualItem };

/**
 * The drawn items in order, with a spacer for each run of undrawn rows before,
 * between and after them. The leading spacer is always present: it marks where
 * the rows start. `totalSize` excludes `scrollMargin`, as the virtualizer
 * reports it.
 */
export function windowSegments(
  items: VirtualItem[],
  scrollMargin: number,
  totalSize: number,
): WindowSegment[] {
  const segments: WindowSegment[] = [];
  let cursor = scrollMargin;
  items.forEach((item, i) => {
    const gap = Math.max(0, item.start - cursor);
    if (i === 0 || gap > 0) {
      segments.push({
        kind: 'spacer',
        id: `gap:${item.index}`,
        height: gap,
        start: i === 0,
      });
    }
    segments.push({ kind: 'item', item });
    cursor = item.end;
  });
  if (items.length === 0) {
    segments.push({ kind: 'spacer', id: 'gap:start', height: 0, start: true });
  }
  segments.push({
    kind: 'spacer',
    id: 'gap:end',
    height: Math.max(0, totalSize + scrollMargin - cursor),
    start: false,
  });
  return segments;
}

/**
 * The window's indexes plus `keep` (the row holding focus) when it lies
 * outside: unmounting a focused input would drop its edit and blur-save.
 */
export function rangeWithIndex(range: Range, keep: number): number[] {
  const indexes = defaultRangeExtractor(range);
  if (keep < 0 || keep >= range.count || indexes.includes(keep)) return indexes;
  return [...indexes, keep].sort((a, b) => a - b);
}

/** The row id (`data-row-id`) of the body row holding focus, or null. */
function useFocusedRowId(wrapper: HTMLElement | null): string | null {
  const [focusedId, setFocusedId] = useState<string | null>(null);
  useEffect(() => {
    if (wrapper === null) return;
    const onFocusIn = (event: FocusEvent) => {
      const row =
        event.target instanceof Element
          ? event.target.closest('tr[data-row-id]')
          : null;
      setFocusedId(row?.getAttribute('data-row-id') ?? null);
    };
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (!(next instanceof Node) || !wrapper.contains(next)) {
        setFocusedId(null);
      }
    };
    wrapper.addEventListener('focusin', onFocusIn);
    wrapper.addEventListener('focusout', onFocusOut);
    return () => {
      wrapper.removeEventListener('focusin', onFocusIn);
      wrapper.removeEventListener('focusout', onFocusOut);
    };
  }, [wrapper]);
  return focusedId;
}

function withDefaults(options: DataTableVirtualization | undefined) {
  return {
    estimateRowHeight: options?.estimateRowHeight ?? 0,
    overscan: options?.overscan ?? DEFAULT_OVERSCAN,
  };
}

/** Index of the item whose key is `target`, or -1 (also for a null target). */
export function indexOfKey(
  count: number,
  getItemKey: (index: number) => string,
  target: string | null,
): number {
  if (target === null) return -1;
  for (let i = 0; i < count; i++) if (getItemKey(i) === target) return i;
  return -1;
}

/**
 * Distance from the top of the scrolling content to the first row: whatever
 * sits above the rows inside the scroll element (headers, toolbars, the table
 * head). Measured on mount, when the rows change, and whenever the scrolling
 * content resizes, since content above the rows can change height.
 */
function useScrollMargin(
  scrollElement: HTMLElement | null | undefined,
  start: HTMLElement | null,
  count: number,
): number {
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    if (!scrollElement || !start) return;
    const measure = () => {
      const margin =
        start.getBoundingClientRect().top -
        scrollElement.getBoundingClientRect().top +
        scrollElement.scrollTop;
      setScrollMargin((prev) => (Math.abs(prev - margin) < 1 ? prev : margin));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scrollElement);
    for (const child of scrollElement.children) observer.observe(child);
    return () => observer.disconnect();
  }, [scrollElement, start, count]);
  return scrollMargin;
}

type VirtualRows =
  /** Draw every row: virtualization is off, or nothing scrolls the table. */
  | { mode: 'all' }
  /** Draw only the windowed items, with spacers standing in for the rest. */
  | {
      mode: 'window';
      items: VirtualItem[];
      segments: WindowSegment[];
      measureElement: (el: Element | null) => void;
    };

/**
 * Windows a table body to the rows inside its scroll container's viewport.
 * The table scrolls with whatever contains it (a page section, a panel), so
 * that ancestor is the scroll element, and everything above the first row
 * inside it (headers, toolbars, the table head) is the scroll margin.
 */
export function useVirtualRows({
  options,
  count,
  getItemKey,
}: {
  options: DataTableVirtualization | undefined;
  count: number;
  getItemKey: (index: number) => string;
}): {
  rows: VirtualRows;
  /** Callback ref for the spacer row that marks where the rows start. */
  setStart: (el: HTMLTableRowElement | null) => void;
  /** The table's own wrapper, once mounted; its scrolling ancestor scrolls the rows. */
  wrapper: HTMLElement | null;
  /**
   * Callback ref for that wrapper. Undefined without virtualization, so other
   * tables skip the extra render a state-backed ref costs.
   */
  wrapperRef: ((el: HTMLElement | null) => void) | undefined;
} {
  const enabled = options !== undefined;
  const { estimateRowHeight, overscan } = withDefaults(options);
  // State, not a ref: the virtualizer needs the element during render.
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null);
  const wrapperRef = enabled ? setWrapper : undefined;
  // The mounted wrapper of a virtualized table; null otherwise.
  const host = enabled ? wrapper : null;
  // undefined until the wrapper is mounted; null when nothing scrolls it.
  const scrollElement = useMemo(
    () => (host ? findScrollParent(host) : undefined),
    [host],
  );
  const [start, setStart] = useState<HTMLTableRowElement | null>(null);
  const scrollMargin = useScrollMargin(scrollElement, start, count);
  // A row being edited stays drawn when it scrolls out of view.
  const focusedId = useFocusedRowId(host);
  const focusedIndex = useMemo(
    () => indexOfKey(count, getItemKey, focusedId),
    [focusedId, count, getItemKey],
  );
  const rangeExtractor = useCallback(
    (range: Range) => rangeWithIndex(range, focusedIndex),
    [focusedIndex],
  );

  // react-doctor-disable-next-line react-hooks-js/incompatible-library -- TanStack Virtual returns unmemoizable functions by design; only this hook skips compilation.
  const virtualizer = useVirtualizer({
    count: scrollElement ? count : 0,
    getScrollElement: () => scrollElement ?? null,
    estimateSize: () => estimateRowHeight,
    overscan,
    scrollMargin,
    getItemKey,
    rangeExtractor,
  });

  if (!enabled || scrollElement === null)
    return { rows: { mode: 'all' }, setStart, wrapper, wrapperRef };
  // Before the lookup (first commit) draw no rows rather than all of them;
  // the layout effect above re-renders before the browser paints.
  const items =
    scrollElement === undefined ? [] : virtualizer.getVirtualItems();
  return {
    rows: {
      mode: 'window',
      items,
      segments: windowSegments(items, scrollMargin, virtualizer.getTotalSize()),
      measureElement: virtualizer.measureElement,
    },
    setStart,
    wrapper,
    wrapperRef,
  };
}

interface LockedWidths {
  layoutKey: unknown;
  containerWidth: number;
  sizing: Record<string, number>;
}

/**
 * Column widths for a virtualized table. Auto table layout sizes columns to
 * the rows in the DOM, so a windowed body would shift its columns as rows
 * scroll in and out. Once rows are drawn, this measures the header cells and
 * returns their widths for a fixed layout; it measures again when the rows
 * change (`layoutKey`) or the container is resized. Null while measuring.
 */
export function useLockedColumnWidths({
  enabled,
  hasRows,
  layoutKey,
  wrapper,
}: {
  enabled: boolean;
  hasRows: boolean;
  layoutKey: unknown;
  wrapper: HTMLElement | null;
}): Record<string, number> | null {
  const [locked, setLocked] = useState<LockedWidths | null>(null);
  const current = locked?.layoutKey === layoutKey ? locked : null;

  // Not derivable in render: the widths are read from the laid-out DOM.
  // react-doctor-disable-next-line react-doctor/no-derived-state-effect
  useLayoutEffect(() => {
    if (!enabled || !hasRows || current !== null || wrapper === null) return;
    const sizing: Record<string, number> = {};
    wrapper
      .querySelectorAll<HTMLTableCellElement>('thead th[data-column-id]')
      .forEach((th) => {
        const id = th.dataset.columnId;
        // Floor: rounded-up widths can sum past the container and scroll.
        if (id) sizing[id] = Math.floor(th.getBoundingClientRect().width);
      });
    // Measure-then-set is what a layout effect is for: it runs before paint,
    // so the auto-layout pass never shows.
    // react-doctor-disable-next-line react-hooks-js/set-state-in-effect
    setLocked({ layoutKey, containerWidth: wrapper.clientWidth, sizing });
  }, [enabled, hasRows, current, layoutKey, wrapper]);

  useLayoutEffect(() => {
    if (current === null || wrapper === null) return;
    const observer = new ResizeObserver(() => {
      if (wrapper.clientWidth !== current.containerWidth) setLocked(null);
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [current, wrapper]);

  return current?.sizing ?? null;
}

/** How a table lays out its columns: widths, fixed or auto, total width. */
export interface ColumnSizingLayout {
  sizing: Record<string, number>;
  fixed: boolean;
  width: number;
}

/**
 * A resizable table's own sizing (set once the user drags a column edge)
 * wins; otherwise a virtualized table holds the widths it measured; otherwise
 * columns size to their content.
 */
export function resolveColumnSizing({
  locked,
  stateSizing,
  isResizable,
  totalSize,
}: {
  locked: Record<string, number> | null;
  stateSizing: Record<string, number>;
  isResizable: boolean;
  totalSize: number;
}): ColumnSizingLayout {
  if (isResizable && Object.keys(stateSizing).length > 0) {
    return { sizing: stateSizing, fixed: true, width: totalSize };
  }
  if (locked !== null) {
    const width = Object.values(locked).reduce((sum, w) => sum + w, 0);
    return { sizing: locked, fixed: true, width };
  }
  return { sizing: stateSizing, fixed: false, width: totalSize };
}
